import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, RefreshCw, Loader2, Eye, Pencil } from "lucide-react";

import PublicNav from "@/components/PublicNav";
import HeroSection from "@/pages/landing/HeroSection";
import PartnersSection from "@/pages/landing/PartnersSection";
import AboutSection from "@/pages/landing/AboutSection";
import LiveOpportunitySection from "@/pages/landing/LiveOpportunitySection";
import JourneySection from "@/pages/landing/JourneySection";
import RolesEcosystemSection from "@/pages/landing/RolesEcosystemSection";
import ImpactSection from "@/pages/landing/ImpactSection";
import ClosingSection from "@/pages/landing/ClosingSection";
import SimpleFooter from "@/pages/landing/SimpleFooter";

export default function LandingPreview() {
  const navigate = useNavigate();
  const [oppCount, setOppCount] = useState(null);
  const [opps, setOpps] = useState([]);
  const [landing, setLanding] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [oppsRes, landingRes] = await Promise.all([
        api.get("/opportunities?status=published"),
        api.get("/public/landing"),
      ]);
      const list = oppsRes.data.opportunities || [];
      setOpps(list);
      setOppCount(list.length);
      setLanding(landingRes.data);
      if (!silent) toast.success("تم تحديث المعاينة بأحدث بيانات");
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadAll(true); }, [loadAll]);

  const submitSearch = (e) => {
    e?.preventDefault();
    toast.info("البحث مُعطَّل في وضع المعاينة");
  };

  return (
    <div className="min-h-screen bg-cream-50" data-testid="landing-preview-page">
      {/* Preview toolbar */}
      <div
        className="fixed top-3 inset-x-0 z-[9999] flex justify-center px-3 pointer-events-none"
        aria-label="شريط أدوات المعاينة"
      >
        <div className="pointer-events-auto flex items-center gap-2 px-3 py-2 rounded-full bg-slate-900/95 backdrop-blur-md text-white shadow-2xl border border-white/10" data-testid="preview-toolbar">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/20 text-amber-200 text-xs font-bold">
            <Eye className="h-3.5 w-3.5" /> وضع المعاينة
          </span>
          <button
            onClick={() => navigate("/dashboard/landing")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors"
            data-testid="preview-back-editor"
          >
            <Pencil className="h-4 w-4" />
            العودة إلى التحرير
          </button>
          <button
            onClick={() => loadAll(false)}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold transition-colors disabled:opacity-60"
            data-testid="preview-refresh"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            حفظ وتحديث المعاينة
          </button>
        </div>
      </div>

      {/* Full landing (read-only in preview) */}
      <div className="pt-14">
        <PublicNav />
        <HeroSection
          oppCount={oppCount}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          submitSearch={submitSearch}
        />
        <PartnersSection partners={landing?.partners_list} />
        <AboutSection />
        <LiveOpportunitySection opps={opps} />
        <JourneySection />
        <RolesEcosystemSection />
        <ImpactSection landing={landing} />
        <ClosingSection />
        <SimpleFooter />
      </div>

      {/* Floating back button (bottom-left) as an easy return */}
      <button
        onClick={() => navigate("/dashboard/landing")}
        className="fixed bottom-6 start-6 z-[9998] inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-slate-900 font-semibold shadow-2xl border border-slate-200 hover:bg-cream-50"
        data-testid="preview-back-editor-floating"
      >
        <ArrowRight className="h-4 w-4" />
        العودة إلى التحرير
      </button>
    </div>
  );
}
